import React from 'react';

interface CityData {
  city: string;
  state: string;
  memberCount: number;
  zipCount: number;
  primaryPercentage: number;
  coordinates?: { lat: number; lng: number } | null;
}

interface InteractiveMapProps {
  cities: CityData[];
}

const InteractiveMap: React.FC<InteractiveMapProps> = ({ cities }) => {
  const winnsboroCenter = { lat: 32.9632, lng: -95.2902 };
  
  // Filter cities that have coordinates
  const citiesWithCoords = cities.filter(city => city.coordinates);
  
  // Calculate max count for scaling
  const maxCount = Math.max(...citiesWithCoords.map(city => city.memberCount));
  
  return (
    <div className="w-full h-full">
      {/* Interactive Map using SVG */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg border shadow-sm overflow-hidden relative" style={{ height: '500px' }}>
        {/* Map Background */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" className="text-gray-300">
            <defs>
              <pattern id="mapGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#mapGrid)" />
          </svg>
        </div>
        
        {/* Map Title */}
        <div className="absolute top-4 left-4 bg-white rounded-lg px-3 py-2 shadow-lg z-10">
          <h3 className="text-sm font-semibold text-gray-900">East Texas Region</h3>
          <p className="text-xs text-gray-600">Centered on Winnsboro, TX</p>
        </div>
        
        {/* City Circles */}
        {citiesWithCoords.map((city) => {
          const intensity = city.memberCount / maxCount;
          const size = Math.max(20, Math.min(60, city.memberCount / 30)); // Dynamic circle size
          
          // Calculate position on the map (scaled to fit the container)
          const mapScale = 0.12; // Scale factor for positioning
          const x = 50 + (city.coordinates!.lng - winnsboroCenter.lng) * mapScale * 100;
          const y = 50 + (winnsboroCenter.lat - city.coordinates!.lat) * mapScale * 100;
          
          // Determine circle color based on intensity
          let circleColor = '#10b981'; // green-500
          if (intensity > 0.8) circleColor = '#ef4444'; // red-500
          else if (intensity > 0.6) circleColor = '#f97316'; // orange-500
          else if (intensity > 0.4) circleColor = '#eab308'; // yellow-500
          else if (intensity > 0.2) circleColor = '#f59e0b'; // amber-500
          
          return (
            <div
              key={`${city.city}-${city.state}`}
              className="absolute rounded-full border-2 border-white shadow-lg cursor-pointer hover:scale-110 transition-transform duration-200 z-20"
              style={{
                left: `${Math.max(5, Math.min(95, x))}%`,
                top: `${Math.max(5, Math.min(95, y))}%`,
                width: `${size}px`,
                height: `${size}px`,
                backgroundColor: circleColor,
                transform: 'translate(-50%, -50%)'
              }}
              title={`${city.city.charAt(0).toUpperCase() + city.city.slice(1).toLowerCase()}, ${city.state}: ${city.memberCount} people`}
            >
              <div className="flex items-center justify-center h-full text-white text-xs font-bold">
                {city.memberCount}
              </div>
            </div>
          );
        })}
        
        {/* Winnsboro Center Marker */}
        <div 
          className="absolute w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow-lg z-20"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}
          title="Winnsboro, TX - Grace Fellowship Location"
        />
        
        {/* Map Legend */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg p-3 shadow-lg z-10">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">People Count</h4>
          <div className="flex items-center space-x-3 text-xs">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Low</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <span>Medium</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span>High</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>Very High</span>
            </div>
          </div>
        </div>
        
        {/* Scale Indicator */}
        <div className="absolute bottom-4 right-4 bg-white rounded-lg p-2 shadow-lg text-xs text-gray-600 z-10">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-1 bg-gray-400"></div>
            <span>~15 miles</span>
          </div>
        </div>
      </div>
      
      {/* City Details Table */}
      <div className="mt-4 bg-white rounded-lg p-4 shadow-sm border">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">City Details</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-medium text-gray-700">City</th>
                <th className="text-right py-2 font-medium text-gray-700">People</th>
                <th className="text-right py-2 font-medium text-gray-700">ZIP Code</th>
                <th className="text-right py-2 font-medium text-gray-700">Primary %</th>
              </tr>
            </thead>
            <tbody>
              {citiesWithCoords.slice(0, 10).map((city) => {
                const intensity = city.memberCount / maxCount;
                let colorClass = 'text-green-600';
                if (intensity > 0.8) colorClass = 'text-red-600';
                else if (intensity > 0.6) colorClass = 'text-orange-600';
                else if (intensity > 0.4) colorClass = 'text-yellow-600';
                
                return (
                  <tr key={`${city.city}-${city.state}`} className="border-b border-gray-100">
                    <td className="py-2 text-gray-900">
                      {city.city.charAt(0).toUpperCase() + city.city.slice(1).toLowerCase()}, {city.state}
                    </td>
                    <td className={`py-2 text-right font-medium ${colorClass}`}>
                      {city.memberCount}
                    </td>
                    <td className="py-2 text-right text-gray-600">
                      {city.zipCount}
                    </td>
                    <td className="py-2 text-right text-gray-600">
                      {city.primaryPercentage}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;
